import React from 'react';
import { motion } from 'framer-motion';
import type { OptionIcon, Poll } from '../types/teaser';
interface PollBlockProps {
  poll: Poll;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Read-only recap of an earlier answer. */
  readOnly?: boolean;
  /** Show only the peer-share summary, without the answer options. */
  summaryOnly?: boolean;
  /** Never show the peer-share summary (the reward lives on the next screen). */
  hideSummary?: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;
function OptionGlyph({
  icon
}: {
  icon: OptionIcon;
}) {
  return <svg viewBox="0 0 20 20" className="h-5 w-5 shrink-0 text-navy2" aria-hidden="true" data-id="element-144" data-mp="2kqtz0mucoe5m">
      {icon === 'branch' && <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" data-id="element-145" data-mp="2fr7hi17hobr0">
          <path d="M3 10h5" data-id="element-146" data-mp="co6xw50sm4" />
          <path d="M8 10 13 5" data-id="element-147" data-mp="3w4xs7qt45y9m" />
          <path d="M8 10 13 15" data-id="element-148" data-mp="2pepzfyobas" />
          <circle cx="15" cy="4" r="1.6" fill="currentColor" stroke="none" data-id="element-149" data-mp="3r75x6nx69w17" />
          <circle cx="15" cy="16" r="1.6" fill="currentColor" stroke="none" data-id="element-150" data-mp="3r98ln3iofh3t" />
        </g>}
      {icon === 'dot' && <g fill="none" stroke="currentColor" strokeWidth="1.3" data-id="element-151" data-mp="2mpzj1at5udst">
          <circle cx="10" cy="10" r="6.5" strokeDasharray="2 2.5" data-id="element-152" data-mp="2mslp160ekl8x" />
          <circle cx="10" cy="10" r="2.2" fill="currentColor" stroke="none" data-id="element-153" data-mp="2rqwxpdeyoppk" />
        </g>}
      {icon === 'break' && <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-154" data-mp="2vp31qdof0r6s">
          <path d="M2 10h5.5" data-id="element-155" data-mp="1z2rdqsvqf4qs" />
          <path d="M12.5 10H18" data-id="element-156" data-mp="2401182zk96bz" />
        </g>}
    </svg>;
}
export function PollBlock({
  poll,
  selected,
  onSelect,
  readOnly = false,
  summaryOnly = false,
  hideSummary = false
}: PollBlockProps) {
  const revealed = selected !== null;
  const mine = poll.options.find(o => o.id === selected);
  return <div className="flex flex-col gap-2" data-id="element-157" data-mp="1gayhmpld93eu">
      {!summaryOnly && poll.options.map(opt => {
      const isMine = selected === opt.id;
      return <button key={opt.id} type="button" onClick={() => !revealed && !readOnly && onSelect(opt.id)} aria-pressed={isMine} className={`rounded-xl border px-4 py-3.5 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue ${revealed ? isMine ? 'cursor-default border-blue bg-blue/[0.08]' : 'cursor-default border-line bg-paper opacity-45' : 'border-line bg-mist hover:border-blue'}`} data-id="element-158" data-mp="1k9nwkmm1yt0l">
            <span className="flex items-center gap-3" data-id="element-159" data-mp="2dory1a5j7fi9">
              {opt.icon && <OptionGlyph icon={opt.icon} data-id="element-160" data-mp="2dq31f8v2el8u" />}
              <span className="flex-1 text-[13px] font-medium leading-snug text-navy" data-id="element-161" data-mp="29neputeyv086">
                {opt.label}
              </span>
            </span>
          </button>;
    })}

      {mine && !hideSummary && <motion.div initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="mt-2 rounded-xl border border-line bg-mist px-4 py-3" data-id="element-162" data-mp="3l1a6k6lirh1i">
          <div className="flex items-baseline gap-2" data-id="element-163" data-mp="1dgpjam0hdhe">
            <span className="font-display text-[30px] font-bold leading-none tabular-nums text-blue" data-id="element-164" data-mp="3ioq6wqfim4dj">
              {mine.share}%
            </span>
            <span className="text-[12px] leading-snug text-mut" data-id="element-165" data-mp="3nokxstzaityl">{poll.resultNote}</span>
          </div>
          <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-line" data-id="element-166" data-mp="f9aob0t2mmgs">
            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: mine.share / 100
        }} transition={{
          duration: 0.28,
          delay: 0.06,
          ease: EASE
        }} style={{
          transformOrigin: 'left'
        }} className="h-full bg-blue" data-id="element-167" data-mp="k841es4g4bgf" />
          </div>
        </motion.div>}
    </div>;
}